<template>
  <full-container v-if="fields">
    <div
      class="banner content-center"
      role="img"
      :style="{
        backgroundImage: `url('${bannerImage.url}')`
      }"
    >
      <div
        class="content-constraint"
        style="position: relative;"
      >
        <div
          v-if="fields"
          class="d-flex align-center"
          style="min-height: 444px"
        >
          <v-col
            cols="12"
            md="6"
            class="banner-content"
          >
            <h1
              v-if="fields.name"
              class="secondary--text"
            >
              {{ fields.name }}
            </h1>
            <ctf-rich-text
              v-if="fields.description"
              class="pt-5"
              :document="fields.description"
            />
          </v-col>
        </div>
        <div
          v-if="!isPast && fields && fields.date"
          class="date d-flex flex-column justify-center align-center white--text"
          :class="color"
        >
          <strong>{{ fields.date | formatDate("DD") }}</strong>
          <span>{{ fields.date | formatDate("MMM") }}</span>
        </div>
      </div>
    </div>
    <v-row
      v-if="!isPast"
      no-gutters
      :class="bannerColor"
      style="height: 150px;"
      justify="center"
      align="center"
    >
      <base-button
        :inverted="bannerColor === 'error'"
        text="Register for this event"
        @click="dialog = true"
      />
    </v-row>
    <div class="content-center">
      <div class="content-constraint">
        <v-row>
          <v-col
            cols="12"
            sm="9"
          >
            <section class="px-3">
              <h2 class="secondary--text mb-5">
                About this event
              </h2>
              <ctf-rich-text :document="description" />
              <base-button
                v-if="!isPast"
                text="Register for this event"
                @click="dialog = true"
              />
            </section>
          </v-col>
          <v-col
            cols="12"
            sm="3"
          >
            <div class="px-3">
              <section v-if="fields.date">
                <h2 class="secondary--text h5">
                  Date and Time
                </h2>
                <p>{{ date }}</p>
                <strong v-if="isPast">This is a past event</strong>
              </section>
              <section
                v-if="fields.location"
                class="my-10"
              >
                <h2 class="secondary--text h5">
                  Location
                </h2>
                <p>{{ fields.location }}</p>
              </section>
              <section v-if="isVirtual">
                <h2 class="secondary--text h5">
                  Watch virtually
                </h2>
                <p>{{ fields.location }}</p>
                <a
                  v-if="fields.link"
                  @click="dialog = true"
                >Learn more</a>
              </section>
            </div>
          </v-col>
        </v-row>
      </div>
    </div>
    <standalone-video
      v-if="recordingMedia"
      :fields="recordingMedia.fields"
      :assets="recordingMedia.assets"
    />
    <div
      v-if="isPast"
      class="content-center"
    >
      <div class="content-constraint">
        <contact-form

          :fields="contactFormFields"
        />
      </div>
    </div>
    <template v-else>
      <v-divider />
      <v-container>
        <event-item-group
          :fields="eventGroupFields"
          only-similar
          :tags="fields.tags"
        />
      </v-container>
    </template>
    <external-link-dialog
      v-if="fields.link"
      :link="fields.link"
      :dialog="dialog"
      @close="dialog = false"
    />
  </full-container>
</template>

<script>
import { mapState } from 'vuex'
import FullContainer from '@/containers/full-container'
import { queryCTF, getCTFAsset } from '../plugins/contentful'
import StandaloneVideo from '@/components/slices/content/standalone-video'
import ExternalLinkDialog from '@/components/dialogs/external-link-dialog'
import ContactForm from '@/components/slices/forms/contact-form'
import EventItemGroup from '@/components/slices/events/event-item-group'
import * as moment from 'moment'

export default {
  name: 'EventDetails',
  metaInfo: {
    title: 'Event Details',
    titleTemplate: '%s - Covantage'
  },
  components: {
    FullContainer,
    StandaloneVideo,
    ExternalLinkDialog,
    ContactForm,
    EventItemGroup
  },
  data () {
    return {
      isPast: false,
      bannerImage: {
        url: '',
        fileName: ''
      },
      recordingMedia: null,
      fields: null,
      dialog: false,
      contactFormFields: {
        heading: 'Stay in Touch',
        subHeading:
          'Want to stay in the loop about Medicare Advantage plans and when to enroll? Sign up today.'
      },
      eventGroupFields: {
        heading: 'Similar events',
        subHeading:
          'Learn more about upcoming educational events hosted by NextBlue and leanr more about what Medicare plan is right for you'
      }
    }
  },
  computed: {
    ...mapState({ siteConfig: state => state.site.siteConfig }),
    date () {
      return this.fields.date ? moment(this.fields.date).format('LLLL') : ''
    },
    isVirtual () {
      return this.fields.tags && this.fields.tags.includes('virtual')
    },
    description () {
      return this.fields.longDescription
        ? this.fields.longDescription
        : this.fields.description
    },
    bannerColor () {
      return this.fields.registerBannerColor &&
      this.fields.registerBannerColor === 'yellow'
        ? 'gold'
        : 'error'
    },
    color () {
      return this.siteConfig.navBottomBorderColor === 'orange'
        ? 'error'
        : 'accent'
    }
  },
  async created () {
    const id = this.$route.params.id
    const { items, includes } = await queryCTF(
      `&include=2&content_type=event&select.fields&sys.id=${id}`
    )
    this.fields = items[0].fields
    this.isPast = new Date(this.fields.date) < new Date()
    const { Asset, Entry } = includes
    const file = getCTFAsset(this.fields.bannerImage.sys.id, Asset)
    const { url, fileName } = file.fields.file
    this.bannerImage = { url, fileName }
    if (Entry && this.fields.recordingMedia) {
      const media = Entry.find(
        e => e.sys.id === this.fields.recordingMedia.sys.id
      )
      if (media) {
        this.recordingMedia = {
          fields: media.fields,
          assets: Asset
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../styles/variables";
.banner {
  min-height: 444px;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  position: relative;

  .banner-content {
    text-align: left;
    padding: 33px 41px;
    background-color: rgba($white, 0.8);
  }

  .date {
    position: absolute;
    top: 50px;
    right: 5%;
    width: 60px;
    height: 60px;
    border-radius: 100%;
  }
}
</style>
